import { useCallback, useRef } from "react";
import { useTranslationEditor } from "../hooks/useTranslationEditor";
import { getMachineTranslations, upsertMachineTranslation } from "../httpService";
import Button from "./button.jsx";
import MarkdownEditor from "./markdownEditor.jsx";

export default function MachineTranslationEditor({ machineId, uploadImageFn, reinitialize }) {
  const emptyForm = useCallback(
    () => ({ title: "", description: "", attributes: [], details: [], extras: [] }),
    []
  );
  const {
    languages,
    activeLocale,
    setActiveLocale,
    form,
    updateForm,
    saving,
    status,
    handleSave,
    translations,
  } = useTranslationEditor({
    entityId: machineId,
    fetchTranslations: getMachineTranslations,
    upsertTranslation: upsertMachineTranslation,
    emptyForm,
    reinitialize,
  });

  if (languages.length === 0) {
    return (
      <p className="text-sm text-text-muted">
        Inga ytterligare språk konfigurerade. Lägg till språk under Webbinnehåll.
      </p>
    );
  }

  return (
    <div data-testid="translation-editor">
      {/* Locale tabs */}
      <div className="flex gap-2 mb-6">
        {languages.map((lang) => {
          const hasTranslation = !!translations[lang.code]?.title;
          return (
            <button
              key={lang.code}
              type="button"
              onClick={() => setActiveLocale(lang.code)}
              className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${activeLocale === lang.code ? "bg-primary text-white border-primary" : "bg-surface border-border-default text-text-body hover:bg-surface-soft"}`}
            >
              <span className="uppercase">{lang.code}</span>
              {!hasTranslation && <span className="ml-1.5 text-xs opacity-60">●</span>}
            </button>
          );
        })}
      </div>

      {form && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="trans-title"
                className="block text-sm font-medium text-text-body mb-1"
              >
                Titel
              </label>
              <input
                id="trans-title"
                type="text"
                value={form.title}
                onChange={(e) => updateForm("title", e.target.value)}
                className="w-full border border-border-default rounded py-2 px-3 text-sm focus:ring-2 focus:ring-primary focus:outline-none"
                data-testid="translation-title"
              />
            </div>
          </div>

          <MarkdownEditor
            id="trans-description"
            label="Beskrivning"
            value={form.description}
            onChange={(e) => updateForm("description", e.target.value)}
            rows={4}
          />

          {/* Attributes */}
          <div>
            <h4 className="text-sm font-medium text-text-body mb-2">Tekniska specifikationer</h4>
            {(form.attributes || []).map((attr, i) => (
              <div key={i} className="flex items-center gap-2 mb-2">
                <input
                  type="text"
                  placeholder="Nyckel"
                  value={attr.key}
                  onChange={(e) => {
                    const a = [...form.attributes];
                    a[i] = { ...a[i], key: e.target.value };
                    updateForm("attributes", a);
                  }}
                  className="flex-1 border border-border-default rounded py-1.5 px-2 text-sm focus:ring-2 focus:ring-primary focus:outline-none"
                />
                <input
                  type="text"
                  placeholder="Värde"
                  value={attr.value}
                  onChange={(e) => {
                    const a = [...form.attributes];
                    a[i] = { ...a[i], value: e.target.value };
                    updateForm("attributes", a);
                  }}
                  className="flex-1 border border-border-default rounded py-1.5 px-2 text-sm focus:ring-2 focus:ring-primary focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => {
                    const a = [...form.attributes];
                    a.splice(i, 1);
                    updateForm("attributes", a);
                  }}
                  className="text-error text-sm"
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                updateForm("attributes", [...(form.attributes || []), { key: "", value: "" }])
              }
              className="text-sm text-primary hover:underline"
            >
              + Lägg till
            </button>
          </div>

          {/* Details */}
          <div>
            <h4 className="text-sm font-medium text-text-body mb-2">Detaljer</h4>
            {(form.details || []).map((d, i) => (
              <TranslationDetailEditor
                key={i}
                item={d}
                index={i}
                label="Detalj"
                field="details"
                form={form}
                updateForm={updateForm}
                uploadImageFn={uploadImageFn}
              />
            ))}
            <button
              type="button"
              onClick={() =>
                updateForm("details", [
                  ...(form.details || []),
                  { title: "", description: "", image: "" },
                ])
              }
              className="text-sm text-primary hover:underline"
            >
              + Lägg till detalj
            </button>
          </div>

          {/* Extras */}
          <div>
            <h4 className="text-sm font-medium text-text-body mb-2">Möjliga tillval</h4>
            {(form.extras || []).map((d, i) => (
              <TranslationDetailEditor
                key={i}
                item={d}
                index={i}
                label="Tillval"
                field="extras"
                form={form}
                updateForm={updateForm}
                uploadImageFn={uploadImageFn}
              />
            ))}
            <button
              type="button"
              onClick={() =>
                updateForm("extras", [
                  ...(form.extras || []),
                  { title: "", description: "", image: "" },
                ])
              }
              className="text-sm text-primary hover:underline"
            >
              + Lägg till tillval
            </button>
          </div>

          <div className="flex items-center gap-4 pt-2">
            <Button size="lg" loading={saving} onClick={handleSave} data-testid="save-translation">
              {`Spara ${activeLocale?.toUpperCase()}`}
            </Button>
            {status === "success" && <span className="text-success text-sm">Sparat!</span>}
            {status === "error" && <span className="text-error text-sm">Kunde inte spara</span>}
          </div>
        </div>
      )}
    </div>
  );
}

function TranslationDetailEditor({ item, index, label, field, form, updateForm, uploadImageFn }) {
  function update(key, value) {
    const arr = [...form[field]];
    arr[index] = { ...arr[index], [key]: value };
    updateForm(field, arr);
  }
  function remove() {
    const arr = [...form[field]];
    arr.splice(index, 1);
    updateForm(field, arr);
  }
  const imageInputRef = useRef(null);
  return (
    <div className="border border-border-default rounded p-3 mb-3">
      <div className="flex justify-between items-center mb-2">
        <span className="text-xs text-text-muted">
          {label} {index + 1}
        </span>
        <button type="button" onClick={remove} className="text-error text-sm">
          ✕
        </button>
      </div>
      <input
        type="text"
        placeholder="Titel"
        value={item.title}
        onChange={(e) => update("title", e.target.value)}
        className="w-full border border-border-default rounded py-1.5 px-2 text-sm mb-2 focus:ring-2 focus:ring-primary focus:outline-none"
      />
      <MarkdownEditor
        rows={2}
        placeholder="Beskrivning"
        value={item.description}
        onChange={(e) => update("description", e.target.value)}
        className="mb-2"
      />
      <div className="flex items-center gap-2">
        {item.image && (
          <img
            src={item.image}
            alt={`${label} ${index + 1}`}
            className="w-16 h-12 object-cover rounded border"
          />
        )}
        <input
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          ref={imageInputRef}
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            try {
              const url = await uploadImageFn(file);
              update("image", url);
            } catch {
              alert("Kunde inte ladda upp bild");
            } finally {
              e.target.value = "";
            }
          }}
        />
        <button
          type="button"
          onClick={() => imageInputRef.current?.click()}
          className="text-xs text-primary hover:underline"
        >
          {item.image ? "Byt bild" : "Ladda upp bild"}
        </button>
      </div>
    </div>
  );
}
